import React, { useContext, useState, useEffect } from "react";
import { Box, Typography, Button, Avatar, LinearProgress } from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate, Link } from "react-router-dom";
import { firestore } from "../firebaseConfig";
import FavoriteIcon from "@mui/icons-material/Favorite";
import TwitterIcon from "@mui/icons-material/Twitter";
import PetsIcon from "@mui/icons-material/Pets";
import UserContext from "../context/userContext";
import GlobalStateContext from "../context/globalStateContext";
import AuthModal from "../components/AuthModal";
import Header from "../components/Header";
import FooterComponent from "../components/Footer";
import { calculateDogTimeRemaining, formatDogAge, groupPledgesByAnimalId } from "../utils/HELPER_FUNCTIONS";

// Using exact styling from DogProfilePage
const PageContainer = styled(Box)({
  width: "100vw",
  maxWidth: "100%",
  minHeight: "100vh",
  backgroundColor: "#000000", // EXACT: Pure black background
  color: "#FFFFFF",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  overflowX: "hidden",
  "&::-webkit-scrollbar": {
    display: "none",
  },
});


const MainContainer = styled(Box)({
  maxWidth: "900px",
  margin: "0 auto",
  padding: "120px 24px",
  // display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  // flex: 1,
  textAlign: "center",
  height: "100%",
  // overflow: "auto",
});

const MissionTitle = styled(Typography)({
  fontSize: "36px",
  fontWeight: "700",
  color: "#FFFFFF",
  marginBottom: "24px",
  lineHeight: "1.1",
});

const MissionSubtitle = styled(Typography)({
  fontSize: "24px",
  color: "#9CA3AF",
  marginBottom: "48px",
  fontWeight: "400",
  maxWidth: "600px",
  margin: "0 auto 48px auto",
});

const BrowseButton = styled(Button)({
  backgroundColor: "#10B981",
  color: "#FFFFFF",
  borderRadius: "10px",
  padding: "16px 32px",
  fontSize: "18px",
  fontWeight: "600",
  textTransform: "none",
  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
  transition: "all 0.2s ease",
  marginBottom: "64px",
  "&:hover": {
    backgroundColor: "#059669",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.35)",
  },
});

const FounderSection = styled(Box)({
  marginBottom: "40px",
});

const FounderText = styled(Typography)({
  fontSize: "16px",
  color: "#9CA3AF",
  marginBottom: "16px",
  fontWeight: "400",
});

const TwitterButton = styled(Button)({
  backgroundColor: "transparent",
  color: "#9CA3AF",
  border: "1px solid #374151",
  borderRadius: "6px",
  padding: "8px 16px",
  fontSize: "14px",
  fontWeight: "400",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#1F2937",
    color: "#FFFFFF",
  },
});

const HorizontalScrollerContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  backgroundColor: "#000000",
  padding: "24px 0",
  [theme.breakpoints.down("md")]: {
    padding: "8px 0",
  },
}));

const ScrollerInner = styled(Box)({
  maxWidth: "800px",
  margin: "0 auto",
  padding: "0 24px",
});

const ShelterLabel = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: "600",
  color: "#6B7280",
  textTransform: "uppercase",
  letterSpacing: "1px",
  marginBottom: "16px",
  [theme.breakpoints.down("md")]: {
    fontSize: "12px",
    marginBottom: "12px",
  },
}));

const DogScrollerWrapper = styled(Box)({
  display: "flex",
  overflowX: "auto",
  gap: "16px",
  paddingBottom: "8px",
  "&::-webkit-scrollbar": {
    height: "6px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#1F2937",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#374151",
    borderRadius: "3px",
    "&:hover": {
      backgroundColor: "#4B5563",
    },
  },
});

const DogScrollerCard = styled(Box)({
  minWidth: "160px",
  maxWidth: "160px",
  backgroundColor: "#1F2937",
  borderRadius: "8px",
  padding: "8px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#374151",
    transform: "translateY(-2px)",
  },
});

const DogCardThumbnail = styled(Avatar)({
  width: "100%",
  height: "140px",
  borderRadius: "6px",
  marginBottom: "8px",
  "& img": {
    objectFit: "cover",
  },
});

const DogCardMetaRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "4px",
  gap: "4px",
});

const DogCardName = styled(Typography)({
  fontSize: "13px",
  fontWeight: "600",
  color: "#FFFFFF",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  flex: 1,
  textAlign: "left",
});

const DogCardAge = styled(Typography)({
  fontSize: "13px",
  fontWeight: "600",
  color: "#FFFFFF",
  whiteSpace: "nowrap",
});

const DogCardTimeRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "4px",
  gap: "4px",
});

const DogCardTimeLabel = styled(Typography)({
  fontSize: "13px",
  fontWeight: "600",
  color: "#FFFFFF",
  whiteSpace: "nowrap",
});

const DogCardTime = styled(Typography)({
  fontSize: "13px",
  fontWeight: "600",
  color: "#FFFFFF",
  fontFamily: "monospace",
  whiteSpace: "nowrap",
});

const DogCardProgressBar = styled(LinearProgress)({
  height: "4px",
  borderRadius: "2px",
  backgroundColor: "#374151",
  "& .MuiLinearProgress-bar": {
    backgroundColor: "#10B981",
    borderRadius: "2px",
  },
});


const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { globalState, setGlobalState } = useContext(GlobalStateContext);
  const [dogsForScroller, setDogsForScroller] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDogs = async () => {
      try {
        // Fetch all dogs from Firestore
        const dogsSnapshot = await firestore
          .collection("web-available-dogs")
          .get();

        let dogs = dogsSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter(dog => dog.euthanasiaStatus !== 'expired' && dog.euthanasiaStatus !== 'rescued');

        // Calculate time remaining for each dog
        dogs = dogs.map(dog => {
          const timeRemaining = calculateDogTimeRemaining(dog, dog.shelter?.closingTime);
          return {
            ...dog,
            ...timeRemaining
          };
        });

        // Sort by time remaining (lowest to highest)
        dogs.sort((a, b) => {
          if (a.timeRemainingMs === -1) return 1;
          if (b.timeRemainingMs === -1) return -1;
          return a.timeRemainingMs - b.timeRemainingMs;
        });

        // Fetch all pledges to populate progress bars
        if (dogs.length > 0) {
          const pledgesSnapshot = await firestore
            .collection("pledges")
            .get();

          const allPledges = pledgesSnapshot.docs.map(doc => doc.data());
          const pledgeMap = groupPledgesByAnimalId(allPledges);

          // Enrich dogs with pledge amounts
          const enrichedDogs = dogs.map(dog => ({
            ...dog,
            pledgedAmount: pledgeMap[dog.animalId] || 0
          }));

          setDogsForScroller(enrichedDogs);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching dogs:", error);
        setIsLoading(false);
      }
    };

    fetchDogs();
  }, []);

  return (
    <PageContainer>
      <Header />
      <MainContainer>
        <MissionTitle>
          359,000 dogs are killed yearly due to space in USA shelters
        </MissionTitle>
        
        <MissionSubtitle>
          We make it easy for rescues to raise funds for saving dogs in urgent need of rescue.
        </MissionSubtitle>

        {/* Horizontal Dog Scroller */}
        {!isLoading && dogsForScroller.length > 0 && (
          <HorizontalScrollerContainer sx={{ marginBottom: "40px", width: "100vw", marginLeft: "calc(-50vw + 50%)" }}>
            <ScrollerInner>
              <ShelterLabel>
                Dogs on Euthanasia List:
              </ShelterLabel>
              <DogScrollerWrapper>
                {dogsForScroller.map((dog) => {
                  const primaryMedia = dog.videos?.[0] || dog.images?.[0];
                  const pledgeGoal = 500;
                  const pledgePercentage = Math.min(((dog.pledgedAmount || 0) / pledgeGoal) * 100, 100);

                  return (
                    <Link
                      to={`/${dog.slug}`}
                      key={dog.id}
                      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                    >
                      <DogScrollerCard>
                        <DogCardThumbnail
                          src={primaryMedia}
                          alt={dog.name}
                          variant="rounded"
                        >
                          {!primaryMedia && <PetsIcon />}
                        </DogCardThumbnail>
                        <DogCardMetaRow>
                          <DogCardName>{dog.name}</DogCardName>
                          <DogCardAge>{formatDogAge(dog.age)}</DogCardAge>
                        </DogCardMetaRow>
                        <DogCardTimeRow>
                          <DogCardTimeLabel>⏰</DogCardTimeLabel>
                          <DogCardTime>
                            {dog.timeRemainingFormatted} LEFT
                          </DogCardTime>
                        </DogCardTimeRow>
                        <DogCardProgressBar
                          variant="determinate"
                          value={Math.max(3, pledgePercentage)}
                        />
                      </DogScrollerCard>
                    </Link>
                  );
                })}
              </DogScrollerWrapper>
            </ScrollerInner>
          </HorizontalScrollerContainer>
        )}

        <FounderSection>
          <FounderText>
            When you pledge money to save a dog, you're only charged when a rescue uploads a verified Proof of Rescue of that dog.
          </FounderText>
          <TwitterButton 
            startIcon={<TwitterIcon />}
            onClick={() => window.open('https://twitter.com/pledgefast_com', '_blank')}
          >
            @pledgefast_com
          </TwitterButton>
        </FounderSection>
      </MainContainer>

      <FooterComponent />
      
      {/* Auth Modal */}
      {globalState.showAuthModal && <AuthModal />}
    </PageContainer>
  );
};

export default LandingPage;